<template>
  <div>
    <!-- 1. Dashboard Section: Highlight & Upcoming -->
    <v-row class="mb-6">
      <!-- Highlight: The Nearest Event -->
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="2" color="indigo-darken-3" v-if="nearestEvent">
          <v-card-item>
            <div class="text-overline mb-1 text-indigo-lighten-4">
              가장 가까운 일정
            </div>
            <div class="text-h4 font-weight-bold mb-2">
              D-{{ calculateDDay(nearestEvent.date).replace('D-', '') }}
            </div>
            <div class="text-h5 font-weight-bold mb-1">
              {{ nearestEvent.title }}
            </div>
            <div class="d-flex align-center text-subtitle-1 text-indigo-lighten-4">
              <v-icon icon="mdi-calendar" size="small" class="mr-2"></v-icon>
              {{ formatDate(nearestEvent.date) }}
            </div>
            <div class="mt-4">
              <v-chip color="white" variant="outlined" label size="small">
                {{ getCategoryName(nearestEvent.categoryId) }}
              </v-chip>
            </div>
          </v-card-item>
        </v-card>
        <v-card v-else class="h-100 pa-4 d-flex align-center justify-center">
          <span class="text-grey">예정된 일정이 없습니다.</span>
        </v-card>
      </v-col>

      <!-- Upcoming List: Top 3 -->
      <v-col cols="12" md="6">
        <v-card class="h-100" elevation="1">
          <v-card-title class="text-h6 font-weight-bold">
            다가오는 일정
          </v-card-title>
          <v-list lines="two">
            <v-list-item
              v-for="event in upcomingEvents"
              :key="event.id"
              @click="openEventDetails(event)"
              rounded="lg"
              class="mb-1"
            >
              <template v-slot:prepend>
                <v-avatar color="grey-lighten-4" rounded="0" size="large">
                  <span class="text-subtitle-1 font-weight-bold text-primary">
                    {{ calculateDDay(event.date) }}
                  </span>
                </v-avatar>
              </template>
              <v-list-item-title class="font-weight-bold">
                {{ event.title }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ formatDate(event.date) }} · {{ getCategoryName(event.categoryId) }}
              </v-list-item-subtitle>
              <template v-slot:append>
                <v-icon icon="mdi-chevron-right" color="grey-lighten-1"></v-icon>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <v-divider class="mb-6"></v-divider>

    <!-- 2. Main List: Grouped by Month -->
    <div v-for="(group, index) in groupedEvents" :key="index" class="mb-6">
      <div class="d-flex align-center mb-3">
        <v-icon icon="mdi-calendar-month" color="primary" class="mr-2"></v-icon>
        <h3 class="text-h5 font-weight-bold text-grey-darken-3">
          {{ group.year }}년 {{ group.month }}월
        </h3>
      </div>

      <v-row>
        <v-col 
          v-for="event in group.events" 
          :key="event.id" 
          cols="12" 
          sm="6" 
          md="4"
          lg="3"
        >
          <v-card 
            class="h-100" 
            hover 
            elevation="1"
            @click="openEventDetails(event)"
            style="border-left: 4px solid;"
            :style="{ borderLeftColor: getCategoryColorHex(event.categoryId) }"
          >
            <v-card-item>
              <div class="d-flex justify-space-between align-start mb-2">
                <span class="text-caption font-weight-bold text-grey-darken-1">
                  {{ formatDateSimple(event.date) }}
                </span>
                <v-chip
                  size="small"
                  :color="getCategoryColor(event.categoryId)"
                  label
                  variant="flat"
                  class="font-weight-bold"
                >
                  {{ calculateDDay(event.date) }}
                </v-chip>
              </div>
              <v-card-title class="text-subtitle-1 font-weight-bold pt-0 text-wrap" style="line-height: 1.3; word-break: keep-all;">
                {{ event.title }}
              </v-card-title>
              <div class="d-flex gap-1 mt-2">
                <span class="text-caption text-medium-emphasis">
                  {{ getCategoryName(event.categoryId) }}
                </span>
                <span v-if="event.target" class="text-caption text-medium-emphasis">
                  · {{ event.target }}
                </span>
              </div>
            </v-card-item>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Event Detail Dialog -->
    <v-dialog v-model="dialog" width="500">
      <v-card v-if="selectedEvent">
        <v-card-title class="text-h5 text-white" :class="getHeaderColor(selectedEvent.categoryId)">
          {{ selectedEvent.title }}
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="mb-4">
            <v-icon icon="mdi-calendar" class="mr-2"></v-icon>
            <strong>일시:</strong> {{ formatDate(selectedEvent.date) }}
            <span class="ml-2 font-weight-bold" :class="'text-' + getCategoryColor(selectedEvent.categoryId)">({{ calculateDDay(selectedEvent.date) }})</span>
          </div>
          <div class="mb-4">
             <v-icon icon="mdi-tag" class="mr-2"></v-icon>
             <strong>구분:</strong> {{ getCategoryName(selectedEvent.categoryId) }}
          </div>
          <div v-if="selectedEvent.target" class="mb-4">
            <v-icon icon="mdi-account-group" class="mr-2"></v-icon>
            <strong>대상:</strong> {{ selectedEvent.target }}
          </div>
          <v-divider class="mb-4"></v-divider>
          <div class="text-body-1">
            {{ selectedEvent.description }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :color="getCategoryColor(selectedEvent.categoryId)" variant="text" @click="dialog = false">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { events, categories } from '../data/events';
import { formatDate, calculateDDay } from '../assets/globalFx';

export default {
  name: 'MainComponents',
  data() {
    return {
      dialog: false,
      selectedEvent: null,
      events: events,
      categories: categories
    }
  },
  computed: {
    // 오늘 이후의 일정만 필터링하고 날짜순 정렬
    sortedEvents() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return this.events
        .filter(event => new Date(event.date) >= today)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },
    // 가장 가까운 일정 (D-Day 임박)
    nearestEvent() {
      return this.sortedEvents.length > 0 ? this.sortedEvents[0] : null;
    },
    // 다가오는 일정 3개 (가장 가까운 것 제외)
    upcomingEvents() {
      return this.sortedEvents.slice(1, 4);
    },
    // 월별로 그룹핑된 일정
    groupedEvents() {
      const groups = {};
      this.sortedEvents.forEach(event => {
        const date = new Date(event.date);
        const key = `${date.getFullYear()}-${date.getMonth() + 1}`; // "2026-3"
        
        if (!groups[key]) {
          groups[key] = {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            events: []
          };
        }
        groups[key].events.push(event);
      });

      // 객체를 배열로 변환하고 시간순 정렬 필요 없음 (sortedEvents가 이미 정렬됨)
      // 하지만 키 순서를 보장하기 위해 정렬된 키 배열을 사용
      return Object.keys(groups)
        // 키가 "2026-3", "2026-10" 형식이므로 숫자로 변환해서 비교해야 함
        // 또는 이미 sortedEvents 순서대로 들어갔으므로 순서는 맞음.
        // Object.values(groups)를 쓰면 브라우저에 따라 순서가 섞일 수 있으니 명시적 정렬 권장
        .sort((a, b) => {
            const [y1, m1] = a.split('-').map(Number);
            const [y2, m2] = b.split('-').map(Number);
            return y1 === y2 ? m1 - m2 : y1 - y2;
        })
        .map(key => groups[key]);
    }
  },
  methods: {
    formatDate,
    calculateDDay,
    formatDateSimple(dateString) {
      const d = new Date(dateString);
      const days = ['일', '월', '화', '수', '목', '금', '토'];
      return `${d.getDate()}일(${days[d.getDay()]})`;
    },
    getCategoryName(id) {
      const category = this.categories.find(c => c.id === id);
      return category ? category.name : '일정';
    },
    getCategoryColor(id) {
      const colors = {
        'csat': 'primary',
        'newyear': 'orange-darken-2',
        'holiday': 'red-darken-2',
        'default': 'grey'
      };
      return colors[id] || colors['default'];
    },
    // 왼쪽 보더용 Hex 코드 (Vuetify 클래스 매핑 대신 직접 색상 지정)
    getCategoryColorHex(id) {
      const colors = {
        'csat': '#1976D2', // primary (blue-darken-2)
        'newyear': '#F57C00', // orange-darken-2
        'holiday': '#D32F2F', // red-darken-2
        'default': '#9E9E9E'
      };
      return colors[id] || colors['default'];
    },
    getHeaderColor(id) {
        const colors = {
        'csat': 'bg-primary',
        'newyear': 'bg-orange-darken-2',
        'holiday': 'bg-red-darken-2',
        'default': 'bg-grey'
      };
      return colors[id] || colors['default'];
    },
    openEventDetails(event) {
      this.selectedEvent = event;
      this.dialog = true;
    }
  }
}
</script>